import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Plus, Settings2, Trash2 } from 'lucide-react';
import api from '../utils/api';
import TemplateModal from '../components/documents/TemplateModal';

export default function Documents() {
  const [templates, setTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await api.get('/documents/templates');
      setTemplates(res.data);
    } catch { }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  async function openEditTemplate(t) {
    try {
      const res = await api.get(`/documents/templates/${t.id}`);
      setEditingTemplate(res.data);
      setShowTemplateModal(true);
    } catch { alert('שגיאה בטעינת תבנית'); }
  }

  async function deleteTemplate(id) {
    if (!window.confirm('למחוק תבנית זו?')) return;
    try {
      await api.delete(`/documents/templates/${id}`);
      loadTemplates();
    } catch { alert('שגיאה במחיקה'); }
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="text-primary-600 dark:text-primary-400" size={22} />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">תבניות מסמכים</h1>
          <span className="text-sm text-gray-500 dark:text-gray-400">({templates.length})</span>
        </div>
        <button
          onClick={() => { setEditingTemplate(null); setShowTemplateModal(true); }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors"
        >
          <Plus size={16} />
          תבנית חדשה
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {templates.length === 0 ? (
          <div className="col-span-3 py-12 text-center text-gray-400 text-sm">
            אין תבניות. צור תבנית חדשה כדי להתחיל.
          </div>
        ) : (
          templates.map((t) => (
            <div key={t.id}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white text-sm">{t.name}</h3>
                  {t.is_default_order && (
                    <span className="text-xs text-primary-600 dark:text-primary-400 font-medium">תבנית הזמנה ברירת מחדל</span>
                  )}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEditTemplate(t)}
                    className="p-1.5 rounded text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20">
                    <Settings2 size={14} />
                  </button>
                  <button onClick={() => deleteTemplate(t.id)}
                    className="p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              {t.description && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t.description}</p>
              )}
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-400">
                {Object.keys(t.variable_mappings || {}).length} משתנים מוגדרים
              </div>
            </div>
          ))
        )}
      </div>

      {showTemplateModal && (
        <TemplateModal
          template={editingTemplate}
          onClose={() => { setShowTemplateModal(false); setEditingTemplate(null); }}
          onSaved={() => { setShowTemplateModal(false); setEditingTemplate(null); loadTemplates(); }}
        />
      )}
    </div>
  );
}
